import { Fragment } from 'react'
import type { ReactNode } from 'react'
import { Icon } from './Icon'
import { GRID_TABLE_CLASSNAME, GRID_TABLE_HEAD_ROW_CLASSNAME, GRID_TABLE_TH_CLASSNAME, GRID_TABLE_ROW_CLASSNAME, GRID_TABLE_TD_CLASSNAME } from '@/styles/tableStyles'

export type SortDir = 'asc' | 'desc'

export type GridColumn<T> = {
  key: string
  header: ReactNode
  render: (row: T) => ReactNode
  align?: 'left' | 'right'
  sortable?: boolean
  thClassName?: string
  tdClassName?: string
  /** Stops the cell's click from also firing the row's `onRowClick` — for a cell with its own controls. */
  stopRowClick?: boolean
  /** How many columns (including this one) this cell should span for a given row — e.g. a "not observed" note replacing several data columns. Columns after it are skipped for that row. */
  colSpan?: (row: T) => number
}

/**
 * `table.grid-tbl` → `grid-table` (process/08-COMPONENT-CATALOGUE.md). One shared component behind
 * every screen's own hand-rolled `<table data-cmp="grid-table">`, instead of the markup, sort
 * arrows and `row-checkbox` column each being reimplemented per screen. Pagination stays out of
 * this component on purpose — rule 25 (`design/24-implementation-rules.md`) makes the cursor
 * `Pager` a footer concern the caller owns, never a page-number control this component could grow.
 */
export function GridTable<T>({
  columns,
  rows,
  getRowKey,
  sort,
  onSortChange,
  selection,
  onRowClick,
  rowClassName,
  showHeader = true,
  emptyMessage = 'Nothing to show.',
  renderExpansion,
}: {
  columns: GridColumn<T>[]
  rows: readonly T[]
  getRowKey: (row: T) => string
  sort?: { key: string; dir: SortDir }
  onSortChange?: (key: string) => void
  selection?: {
    selectedKeys: ReadonlySet<string>
    onToggle: (key: string) => void
    onToggleAll?: (checked: boolean) => void
  }
  onRowClick?: (row: T) => void
  rowClassName?: (row: T) => string
  showHeader?: boolean
  emptyMessage?: string
  /** A panel rendered in its own full-width `<tr>` directly beneath a specific row — e.g. an inline "review" expansion. Return `null`/`undefined` for rows with no panel open. */
  renderExpansion?: (row: T) => ReactNode | null | undefined
}) {
  const allSelected = selection ? rows.length > 0 && rows.every((row) => selection.selectedKeys.has(getRowKey(row))) : false
  const totalColumns = columns.length + (selection ? 1 : 0)

  return (
    <table data-cmp="grid-table" className={GRID_TABLE_CLASSNAME}>
      {showHeader && (
      <thead>
        <tr className={GRID_TABLE_HEAD_ROW_CLASSNAME}>
          {selection && (
            <th className={GRID_TABLE_TH_CLASSNAME} style={{ width: 1 }}>
              {selection.onToggleAll && (
                <input
                  type="checkbox"
                  data-cmp="row-checkbox"
                  checked={allSelected}
                  onChange={(e) => selection.onToggleAll!(e.target.checked)}
                  aria-label="Select all rows"
                />
              )}
            </th>
          )}
          {columns.map((col) => {
            const isSorted = sort?.key === col.key
            const alignClass = col.align === 'right' ? 'text-right' : ''
            return (
              <th
                key={col.key}
                className={`${GRID_TABLE_TH_CLASSNAME} ${alignClass} ${col.thClassName ?? ''}`}
                aria-sort={isSorted ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
              >
                {col.sortable ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 cursor-pointer select-none"
                    onClick={() => onSortChange?.(col.key)}
                  >
                    {col.header}
                    {isSorted && <Icon name={sort.dir === 'asc' ? 'i-chevU' : 'i-chevD'} size="xs" />}
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1">{col.header}</span>
                )}
              </th>
            )
          })}
        </tr>
      </thead>
      )}
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td className={GRID_TABLE_TD_CLASSNAME} colSpan={columns.length + (selection ? 1 : 0)}>
              {emptyMessage}
            </td>
          </tr>
        ) : (
          rows.map((row) => {
            const key = getRowKey(row)
            const clickable = onRowClick ? 'cursor-pointer' : ''
            const expansion = renderExpansion?.(row)
            return (
              <Fragment key={key}>
                <tr
                  className={`${GRID_TABLE_ROW_CLASSNAME} ${clickable} ${rowClassName?.(row) ?? ''}`}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {selection && (
                    <td className={GRID_TABLE_TD_CLASSNAME}>
                      <input type="checkbox" data-cmp="row-checkbox" checked={selection.selectedKeys.has(key)} onChange={() => selection.onToggle(key)} aria-label="Select row" />
                    </td>
                  )}
                  {(() => {
                    let skip = 0
                    return columns.map((col) => {
                      if (skip > 0) {
                        skip -= 1
                        return null
                      }
                      const span = col.colSpan?.(row) ?? 1
                      if (span > 1) skip = span - 1
                      return (
                        <td
                          key={col.key}
                          colSpan={span > 1 ? span : undefined}
                          className={`${GRID_TABLE_TD_CLASSNAME} ${col.align === 'right' ? 'text-right' : ''} ${col.tdClassName ?? ''}`}
                          onClick={col.stopRowClick ? (e) => e.stopPropagation() : undefined}
                        >
                          {col.render(row)}
                        </td>
                      )
                    })
                  })()}
                </tr>
                {expansion != null && (
                  <tr>
                    <td className="p-0" colSpan={totalColumns}>
                      {expansion}
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })
        )}
      </tbody>
    </table>
  )
}
